import { AlertsAPI, AlertsConfigurations } from "@acin.reactjs/acin-ui/alert";
import { FormAPI } from "@acin.reactjs/acin-ui/form";
import i18next from "i18next";
import { FieldValues, Path } from "react-hook-form";
import { ResponseError } from "../../interfaces/BackendInterfaces";
import { FormSetError } from "../../interfaces/BaseAppInterfaces";
import FormErrorsAlert from "./FormErrorsAlert";

export const handleFormErrorAlerts = <T extends FieldValues = FieldValues>(
  errors: NonNullable<ResponseError["errors"]>,
  alerts: AlertsAPI<AlertsConfigurations>,
  formApi?: FormAPI<T>,
) => {
  const parsedErrors: JSX.Element[] = [];

  Object.keys(errors).forEach((key) => {
    const errorMessage =
      errors?.[key]?.[0].length > 1 ? errors[key][0] : errors[key];

    const label = formApi?.getFieldLabel(key);

    if (errorMessage) {
      parsedErrors.push(
        <li>
          {label && (
            <>
              <b>{label}</b>:&nbsp;
            </>
          )}
          {errorMessage}
        </li>,
      );
    }
  });

  if (parsedErrors.length) {
    const shownErrors = parsedErrors.slice(0, 5);

    if (parsedErrors.length > 5) {
      const remaining = parsedErrors.length - 5;
      const message =
        remaining === 1
          ? i18next.t("errors.form.more_one", { count: remaining })
          : i18next.t("errors.form.more_other", { count: remaining });

      shownErrors.push(<li key="more">{message}</li>);
    }

    alerts.error(<FormErrorsAlert shownErrors={shownErrors} />);
  }
};

export const handleBackendErrors = (
  errors: ResponseError["errors"],
  alerts: AlertsAPI<AlertsConfigurations>,
  message: string,
  setError?: FormSetError<FieldValues>,
) => {
  // Case 1: backend did NOT send "errors"
  if (!errors) {
    alerts.error(message);
    return;
  }

  // Case 2: backend sent field-level errors → set form errors
  if (setError) {
    Object.keys(errors).forEach((field) => {
      const cleanedMessages = errors[field].map((msg) =>
        msg.endsWith(".") ? msg.slice(0, -1) : msg,
      );

      setError(field as Path<unknown>, {
        message: `${cleanedMessages.join(", ")}.`,
      });
    });
  }

  // Case 3: errors object has validation messages → show list in alert
  if (Object.keys(errors).length > 0) {
    handleFormErrorAlerts(errors, alerts);
    return;
  }

  // Case 4: empty errors object {} → fallback to general message
  alerts.error(message);
};
